/**
 * Exercise Templates Hook
 * Fetches and combines static exercise templates with Hevy API templates
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { ExerciseTemplateService } from '../../services/exerciseTemplateService'
import { exerciseTemplates } from '../../utils/exercise-dict'
import { exerciseProcessor } from '../../services/exerciseProcessor'
import type { HevyExerciseTemplate, UnifiedExercise } from '../../types'
import { queryKeys } from '../queryKeys'
import { useUserCredentials } from './useCredentials'
import { useUserSettings } from './useSettings'


// Adapter function to convert Hevy exercise template to unified format
const adaptHevyExercise = (hevy: HevyExerciseTemplate): UnifiedExercise => {
  const processed = exerciseProcessor.processExercise({
    templateId: hevy.id,
    name: hevy.title
  })
  return {
    id: hevy.id,
    name: hevy.title,
    primaryMover: processed.primaryMover,
    secondaryMovers: processed.secondaryMovers,
    isCustom: hevy.is_custom,
    source: 'hevy'
  }
}

// Define the static exercise template type
interface StaticExerciseTemplate {
  name: string
  primary_movers: string
  secondary_movers: string[]
  bodyweight: boolean
  assisted: boolean
}

// Adapter function to convert static exercise template to unified format
const adaptStaticExercise = (templateId: string, template: StaticExerciseTemplate): UnifiedExercise => {
  const processed = exerciseProcessor.processExercise({
    templateId,
    name: template.name
  })
  return {
    id: templateId,
    name: template.name,
    primaryMover: processed.primaryMover,
    secondaryMovers: processed.secondaryMovers,
    isCustom: false,
    source: 'static'
  }
}

export const useExerciseTemplates = () => {
  const { data: userSettings } = useUserSettings()
  const { data: userCredentials } = useUserCredentials()

  // Check if user has API data source configured
  const hasApiConfig = Boolean(
    userSettings?.settings.dataSource === 'api' &&
    userCredentials?.apiKey &&
    userCredentials.apiKey !== 'CSV_IMPORT' &&
    userCredentials.apiKey.length > 10
  )

  const apiKeyHash = userCredentials?.apiKey ? userCredentials.apiKey.slice(-8) : ''

  // Fetch Hevy exercise templates (only if API is configured)
  const {
    data: hevyTemplates = [],
    isLoading: isLoadingHevy,
    error: hevyError
  } = useQuery<HevyExerciseTemplate[]>({
    queryKey: queryKeys.exerciseTemplates(apiKeyHash),
    queryFn: async (): Promise<HevyExerciseTemplate[]> => {
      if (!userCredentials?.apiKey) {
        throw new Error('API key not configured')
      }
      return ExerciseTemplateService.fetchAllExerciseTemplates(userCredentials.apiKey)
    },
    enabled: hasApiConfig,
    staleTime: 5 * 60 * 1000, // 5 minutes (exercise templates don't change often)
    retry: false
  })

  // Combine static and API templates
  const unifiedExercises = useMemo(() => {
    const exercises: UnifiedExercise[] = []

    // Add static exercises
    Object.entries(exerciseTemplates).forEach(([templateId, template]) => {
      exercises.push(adaptStaticExercise(templateId, template))
    })

    // Add Hevy exercises (if available)
    if (hevyTemplates.length > 0) {
      hevyTemplates.forEach(hevyTemplate => {
        exercises.push(adaptHevyExercise(hevyTemplate))
      })
    }

    // Sort by name for better UX
    return exercises.sort((a, b) => a.name.localeCompare(b.name))
  }, [hevyTemplates])

  // For Split Builder, only return API exercises
  const apiOnlyExercises = useMemo(() => {
    if (hevyTemplates.length > 0) {
      return hevyTemplates.map(adaptHevyExercise).sort((a, b) => a.name.localeCompare(b.name))
    }
    return []
  }, [hevyTemplates])

  return {
    exercises: unifiedExercises,
    apiOnlyExercises,
    isLoading: isLoadingHevy,
    error: hevyError,
    hasApiData: hevyTemplates.length > 0,
    staticCount: Object.keys(exerciseTemplates).length,
    hevyCount: hevyTemplates.length
  }
}